import type { CustomThemeConfig } from '@iheartjane/reefer';
import { effects, generateTheme, generateThemeCSS } from '@iheartjane/reefer';

import type { BloomMenuTheme } from '@jane/bloom/types/schemas/roots/bloom/menuTheme';

export function bloomContrastColorKey(
  contrast?: string | null
): 'grays-black' | 'grays-white' {
  return contrast === '#0E0E0E' ? 'grays-black' : 'grays-white';
}

function generateBloomThemeCSSVarsMap(theme: BloomMenuTheme) {
  return {
    '--bloom-colors-cbd': theme.bloom?.colors?.lineage?.cbd?.main,
    '--bloom-colors-cbd-contrast': theme.bloom?.colors?.lineage?.cbd?.contrast,
    '--bloom-colors-header-contrast': theme.bloom?.colors?.header?.contrast,
    '--bloom-colors-header-contrast-text-label':
      theme.bloom?.colors?.header?.contrast === '#0E0E0E'
        ? 'var(--colors-grays-white)'
        : 'var(--colors-grays-black)',
    '--bloom-colors-header-highlight':
      theme.bloom?.colors?.header?.contrast === '#0E0E0E'
        ? 'var(--colors-transparent-black-ten)'
        : 'var(--colors-transparent-white-ten)',
    '--bloom-colors-header-main': theme.bloom?.colors?.header?.main,
    '--bloom-colors-hybrid': theme.bloom?.colors?.lineage?.hybrid?.main,
    '--bloom-colors-hybrid-contrast':
      theme.bloom?.colors?.lineage?.hybrid?.contrast,
    '--bloom-colors-indica': theme.bloom?.colors?.lineage?.indica?.main,
    '--bloom-colors-indica-contrast':
      theme.bloom?.colors?.lineage?.indica?.contrast,
    '--bloom-colors-sativa': theme.bloom?.colors?.lineage?.sativa?.main,
    '--bloom-colors-sativa-contrast':
      theme.bloom?.colors?.lineage?.sativa?.contrast,
    '--bloom-colors-secondary-contrast':
      theme.bloom?.colors?.secondary?.contrast ?? '#FFFFFF',
    '--bloom-colors-store-button':
      /** Adjust the primary color */
      theme.bloom?.colors?.header?.contrast === '#FFFFFF'
        ? effects.lighten(theme.bloom?.colors?.primary?.main || '', 0.7)
        : effects.darken(theme.bloom?.colors?.primary?.main || '', 0.7),
    '--bloom-colors-store-card-border':
      theme.bloom?.colors?.header?.contrast === '#FFFFFF'
        ? 'var(--colors-transparent-black-ten)'
        : 'var(--colors-transparent-white-ten)',
    '--bloom-colors-store-skeleton':
      theme.bloom?.colors?.header?.contrast === '#FFFFFF'
        ? 'var(--colors-grays-light)'
        : '#1a1a1a',
    '--bloom-colors-store-skeleton-light':
      theme.bloom?.colors?.header?.contrast === '#FFFFFF'
        ? 'var(--colors-grays-white)'
        : '#202020',
  };
}

export function deriveCustomReeferTheme(
  theme: BloomMenuTheme
): CustomThemeConfig {
  return {
    colors: {
      background: '#FFFFFF',
      info: theme.bloom?.colors?.deals?.main,
      primary: theme.bloom?.colors?.primary?.main ?? '#5e1ff0',
      secondary: theme.bloom?.colors?.secondary?.main ?? '#0E0E0E',
      text: {
        info: theme.bloom?.colors?.deals?.contrast,
        inverse: theme.bloom?.colors?.primary?.contrast ?? '#FFFFFF',
      },
    },
    components: {
      Button: {
        variants: {
          minimal: {
            typographyColor: 'text-main',
          },
          primary: {
            typographyColor: bloomContrastColorKey(
              theme.bloom?.colors?.primary?.contrast
            ),
          },
          secondary: {
            backgroundColor: 'secondary',
            borderColor: 'transparent',
            typographyColor: bloomContrastColorKey(
              theme.bloom?.colors?.secondary?.contrast
            ),
          },
          tertiary: {
            backgroundColor: 'transparent-black-five',
            borderColor: 'transparent',
            typographyColor: 'text-main',
          },
          'tertiary-selected': {
            backgroundColor: 'transparent-black-five',
            typographyColor: 'text-main',
          },
        },
      },
      Typography: {
        fonts: {
          branded: {
            fontFamily: theme.bloom?.fonts?.heading?.font_family,
          },
          default: {
            fontFamily: theme.bloom?.fonts?.body?.font_family,
          },
        },
        variants: {
          header: { branded: true },
          'header-bold': { branded: true },
          title: { branded: true },
          'title-bold': { branded: true },
        },
      },
    },
  };
}

export function buildThemeCssVars(theme: BloomMenuTheme): string {
  const bloomCssVars = generateBloomThemeCSSVarsMap(theme);
  const customTheme = deriveCustomReeferTheme(theme);
  const finalTheme = generateTheme(customTheme);
  let themeCss = generateThemeCSS(finalTheme);

  themeCss += '\n\n:root {';

  Object.entries(bloomCssVars)
    .filter(Boolean)
    .forEach(([key, value]) => {
      themeCss += `\n  ${key}: ${value};`;
    });

  themeCss += `\n}\n\n`;

  return themeCss;
}
